import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabs } from '../context/TabsContext';
import { useWalkthroughChef } from '../context/WalkthroughChefContext';
import FloatingChefTab from './FloatingChefTab';
import FeedbackModal from './FeedbackModal';

const FloatingChef = ({ 
    isListening, 
    isTriggered, 
    isEnabled,
    onToggle,
    isAIThinking,
    isSpeaking,
    agentName = 'Chef'
}) => {
    const { 
        walkthroughMessage, 
        walkthroughSubMessage, 
        walkthroughChefState, 
        isWalkthroughActive,
        walkthroughPosition,
        showNextButton,
        onNextHandler,
        showBackButton,
        onBackHandler
    } = useWalkthroughChef();
    const audioRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { areTabsCollapsed, collapseTabs, expandTabs } = useTabs();
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // Drag and position state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        // Load saved position from localStorage
        try {
            const saved = localStorage.getItem('chefTabPosition');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Validate the position data structure
                if (parsed && typeof parsed === 'object' && 
                    (parsed.edge === 'left' || parsed.edge === 'bottom') &&
                    typeof parsed.offset === 'number' && 
                    parsed.offset >= 0) {
                    
                    // Clamp offset to viewport bounds on initial load
                    if (typeof window !== 'undefined') {
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const buttonSize = 88; // Button is 88px (w-16 h-20 or w-20 h-16)
                        
                        if (parsed.edge === 'left') {
                            // For left edge, offset is vertical (top)
                            const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        } else {
                            // For bottom edge, offset is horizontal (left)
                            const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        }
                    }
                    return parsed;
                }
            }
        } catch (e) {
            console.warn('Failed to parse chefTabPosition from localStorage:', e);
        }
        // Default position: left edge, 30% from top
        return { 
            edge: 'left', 
            offset: typeof window !== 'undefined' 
                ? Math.max(24, Math.round(window.innerHeight * 0.3)) 
                : 24 
        };
    });
    const dragStartRef = useRef({ x: 0, y: 0, startPos: { edge: 'bottom', offset: 0 } });
    const buttonRef = useRef(null);
    
    // Check if we're on the LiveSession page
    const isLiveSession = location.pathname.includes('/live/');

    // Play ding sound when AI starts thinking (end of question detected)
    useEffect(() => {
        if (isAIThinking && audioRef.current) {
            // Create a simple ding sound using Web Audio API
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        }
    }, [isAIThinking]);

    const getChefState = () => {
        if (!isEnabled) return 'disabled';
        if (isSpeaking) return 'talking';
        if (isAIThinking) return 'thinking';
        if (isTriggered) return 'listening';
        if (isListening) return 'listening';
        return 'idle';
    };

    const getChefLogo = (state) => {
        let imageSrc;
        switch (state) {
            case 'listening':
                imageSrc = '/images/Chef-listening.png';
                break;
            case 'thinking':
                imageSrc = '/images/Chef-thinking.png';
                break;
            case 'talking':
                imageSrc = '/images/Chef-talking.png';
                break;
            case 'disabled':
                imageSrc = '/images/Chef.png';
                break;
            default: // idle
                imageSrc = '/images/Chef.png';
                break;
        }
        
        return (
            <div className={`w-full h-full flex items-center justify-center overflow-hidden ${
                position.edge === 'left' ? 'p-1 pr-1 pb-0 pl-2 pt-1' : 'p-1'
            }`}>
                <img 
                    src={imageSrc} 
                    alt={`Chef ${state}`}
                    className="w-full h-full object-contain"
                    style={{ 
                        width: areTabsCollapsed ? '0' : '100%', 
                        height: areTabsCollapsed ? '0' : '100%',
                        maxWidth: '100%', 
                        maxHeight: '100%',
                        display: areTabsCollapsed ? 'none' : 'block'
                    }}
                />
            </div>
        );
    };

    const getPulseAnimation = (state) => {
        // Removed animate-pulse to prevent blinking
        return '';
    };

    const currentState = getChefState();

    const handleChefClick = () => {
        if (isLiveSession) {
            // On LiveSession, use the original toggle behavior
            onToggle();
        } else {
            // On other pages, open feedback modal
            setIsModalOpen(true);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('chefTabPosition', JSON.stringify(position));
    }, [position]);

    // Validate and clamp position when window resizes
    useEffect(() => {
        const validatePosition = () => {
            if (typeof window === 'undefined') return;
            
            setPosition(currentPosition => {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                
                let needsUpdate = false;
                let newPosition = { ...currentPosition };
                
                if (currentPosition.edge === 'left') {
                    const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                } else {
                    const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                }
                
                return needsUpdate ? newPosition : currentPosition;
            });
        };
        
        // Validate on mount and window resize
        validatePosition();
        window.addEventListener('resize', validatePosition);
        
        return () => {
            window.removeEventListener('resize', validatePosition);
        };
    }, []); // Only run on mount, use functional setState to always get latest position

    // Drag handlers
    const handleMouseDown = (e) => {
        // Prevent drag if clicking to open modal (only for non-LiveSession pages)
        if (e.button !== 0) return; // Only left mouse button
        
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPos: { ...position }
        };
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const moveThreshold = 10; // Pixels before considered a drag
        const collapseThreshold = 30; // Distance to trigger collapse

        // Check if dragging towards edge (inward) to collapse tabs
        const startPos = dragStartRef.current.startPos;
        if (startPos && startPos.edge === 'left' && deltaX < -collapseThreshold) {
            // Dragging left (towards edge) - collapse tabs
            collapseTabs();
        } else if (startPos && startPos.edge === 'bottom' && deltaY > collapseThreshold) {
            // Dragging down (towards edge) - collapse tabs
            collapseTabs();
        }

        // Only start dragging if moved beyond threshold
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const currentX = e.clientX;
            const currentY = e.clientY;

            // Determine which edge to snap to based on position
            const leftDistance = currentX;
            const bottomDistance = viewportHeight - currentY;

            // Snap to left edge if closer to left (within left 30% of screen)
            if (leftDistance < viewportWidth * 0.3 && currentY < viewportHeight - 100) {
                const newOffset = Math.max(24, Math.min(viewportHeight - 88, currentY));
                setPosition({ edge: 'left', offset: newOffset });
            } else {
                const newOffset = Math.max(24, Math.min(viewportWidth - 88, currentX));
                setPosition({ edge: 'bottom', offset: newOffset });
            }
        }
    };

    const handleMouseUp = (e) => {
        if (isDragging) {
            const deltaX = e.clientX - dragStartRef.current.x;
            const deltaY = e.clientY - dragStartRef.current.y;
            const moveThreshold = 10;

            // If didn't move much, treat as click
            if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
                // If tabs are collapsed (showing only sliver), always expand them first
                // Do NOT open chef when tabs are in stored/collapsed state
                if (areTabsCollapsed) {
                    expandTabs();
                    return; // Prevent chef from opening
                }
                
                // Only open chef when tabs are expanded
                handleChefClick();
            }
        }
        setIsDragging(false);
    };

    // Add global event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, position]);

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setIsDragging(true);
        dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            startPos: { ...position }
        };
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        const collapseThreshold = 30; // Distance to trigger collapse

        // Check if dragging towards edge (inward) to collapse tabs
        const startPos = dragStartRef.current.startPos;
        if (startPos && startPos.edge === 'left' && deltaX < -collapseThreshold) {
            // Dragging left (towards edge) - collapse tabs
            collapseTabs();
        } else if (startPos && startPos.edge === 'bottom' && deltaY > collapseThreshold) {
            // Dragging down (towards edge) - collapse tabs
            collapseTabs();
        }

        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const currentX = touch.clientX;
            const currentY = touch.clientY;

            const leftDistance = currentX;

            if (leftDistance < viewportWidth * 0.3 && currentY < viewportHeight - 100) {
                const newOffset = Math.max(24, Math.min(viewportHeight - 88, currentY));
                setPosition({ edge: 'left', offset: newOffset });
            } else {
                const newOffset = Math.max(24, Math.min(viewportWidth - 88, currentX));
                setPosition({ edge: 'bottom', offset: newOffset });
            }
        }
        e.preventDefault();
    };

    const handleTouchEnd = (e) => {
        if (isDragging) {
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - dragStartRef.current.x;
            const deltaY = touch.clientY - dragStartRef.current.y;
            const moveThreshold = 10;

            if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
                // If tabs are collapsed (showing only sliver), always expand them first
                // Do NOT open chef when tabs are in stored/collapsed state
                if (areTabsCollapsed) {
                    expandTabs();
                    return; // Prevent chef from opening
                }
                
                // Only open chef when tabs are expanded
                handleChefClick();
            }
        }
        setIsDragging(false);
    };

    // Get dynamic positioning based on edge and offset
    const getDynamicStyle = () => {
        const bodyPadding = 'var(--spacing-sm)';
        const buttonSize = areTabsCollapsed ? 10 : 88; // Use sliver size when collapsed
        
        let safeOffset = position.offset;
        if (typeof window !== 'undefined') {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            if (position.edge === 'left') {
                const maxOffset = Math.max(24, viewportHeight - (areTabsCollapsed ? 10 : 88) - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            } else {
                const maxOffset = Math.max(24, viewportWidth - (areTabsCollapsed ? 10 : 88) - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            }
        }
        
        if (position.edge === 'left') {
            return {
                left: areTabsCollapsed ? '0px' : `calc(0px - ${bodyPadding})`,
                top: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        } else {
            return {
                bottom: areTabsCollapsed ? '0px' : `calc(0px - ${bodyPadding})`,
                left: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        }
    };

    return (
        <>
            <div 
                ref={buttonRef}
                className="fixed z-[9999]"
                style={getDynamicStyle()}
            >
                <button
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    className={`${getPulseAnimation(currentState)} ${
                        position.edge === 'left' 
                            ? `rounded-r-lg ${areTabsCollapsed ? 'w-[10px] h-16' : 'w-20 h-16'}` 
                            : `rounded-t-lg ${areTabsCollapsed ? 'w-16 h-[10px]' : 'w-16 h-20'}`
                    }`}
                    style={{
                        margin: '0 !important',
                        padding: '0 !important',
                        backgroundColor: '#FFFFFF', // White background
                        borderTop: '1px solid #0D0D0D',
                        borderRight: position.edge === 'left' ? '1px solid #0D0D0D' : '1px solid #0D0D0D',
                        borderLeft: position.edge === 'left' ? 'none' : '1px solid #0D0D0D',
                        borderBottom: position.edge === 'left' ? '1px solid #0D0D0D' : 'none',
                        boxShadow: position.edge === 'left' ? '2px 0 2px rgba(0, 0, 0, 0.02)' : '0 -1px 2px rgba(0, 0, 0, 0.02)',
                        position: 'relative',
                        display: 'block',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        opacity: 1
                    }}
                    title={
                        isLiveSession 
                            ? (!isEnabled 
                                ? 'Enable voice companion' 
                                : isAIThinking 
                                    ? `${agentName} is thinking...`
                                    : isSpeaking
                                        ? `${agentName} is responding...`
                                        : isTriggered
                                            ? 'Listening for command...'
                                            : isListening
                                                ? 'Listening...'
                                                : `Voice ready - say "Hey ${agentName}"`)
                            : `Open ${agentName} interface`
                    }
                >
                    {getChefLogo(currentState)}
                </button>
                
                {/* Audio element for ding sound */}
                <audio ref={audioRef} preload="auto" />
            </div>

            {/* Chef Modal - Disabled for now, only works in live sessions */}
            {false && !isLiveSession && isModalOpen && (
                <div 
                    className="fixed inset-0 z-50 bg-neutral-50"
                    onClick={(e) => {
                        // Close modal when clicking on background
                        if (e.target === e.currentTarget) {
                            closeModal();
                        }
                    }}
                >
                    {/* Grid dots background */}
                    <div 
                        className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage: `
                                radial-gradient(circle, #1b263b 1px, transparent 1px)
                            `,
                            backgroundSize: '20px 20px'
                        }}
                    />
                    
                    {/* Close button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            closeModal();
                        }}
                        className="absolute top-6 left-6 text-neutral-900 hover:opacity-60 font-mono text-lg border-none bg-transparent p-0 m-0 z-10 cursor-pointer"
                    >
                        ✕
                    </button>
                    
                    {/* Modal content - placeholder for now */}
                    <div className="relative z-10 flex items-center justify-center h-full">
                        <div className="text-center">
                            <h2 className="font-sans text-3xl font-medium text-neutral-900 mb-4">
                                {agentName} Interface
                            </h2>
                            <p className="font-sans text-base leading-relaxed text-neutral-900 opacity-60">
                                Chef interface features will be implemented here
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback Modal - Opens when clicking chef on non-LiveSession pages */}
            {!isLiveSession && (
                <FeedbackModal 
                    isOpen={isModalOpen} 
                    onClose={() => setIsModalOpen(false)} 
                />
            )}

            {/* Walkthrough Chef Tab - Uses existing FloatingChefTab when walkthrough is active */}
            {isWalkthroughActive && walkthroughMessage && (
                <FloatingChefTab
                    isVisible={isWalkthroughActive}
                    isExpanded={true}
                    onToggle={() => {}}
                    message={walkthroughMessage}
                    subMessage={walkthroughSubMessage}
                    chefState={walkthroughChefState}
                    position={walkthroughPosition}
                    hideChefImage={true}
                    showNextButton={showNextButton}
                    onNext={onNextHandler}
                    showBackButton={showBackButton}
                    onBack={onBackHandler}
                />
            )}
        </>
    );
};

export default FloatingChef;
